import * as dayjsModule from 'dayjs';
const dayjs = dayjsModule.default || dayjsModule;
import { cache } from './cache.js';
import { STORAGE_KEYS, DEFAULT_LOCALE, SUPPORTED_LOCALES } from '../constants/index.js';

/**
 * 格式化日期
 * @param {string | number | Date | dayjs.Dayjs} [date] - 日期值
 * @param {string} [format='YYYY-MM-DD HH:mm:ss'] - 格式模板
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return '';
  return dayjs(date).format(format);
}

// 统一缓存管理器（支持过期时间、前缀隔离、uni-app 兼容）
export { cache, createCacheManager } from './cache.js';

/**
 * 获取指定语言的多语言数据
 *
 * 从多语言对象中提取指定语言代码对应的翻译内容。
 *
 * @param {Object|null|undefined} data - 多语言数据对象，如 { 'zh-CN': { category_name: '电子产品' } }
 * @param {string|null|undefined} locale - 语言代码，如 'zh-CN'
 * @returns {Object|undefined} 指定语言的翻译内容，数据为空/语言为空/没有该语言数据时返回 undefined
 *
 * @example
 * getLocaleValue({ 'zh-CN': { category_name: '电子产品' } }, 'zh-CN')
 * // => { category_name: '电子产品' }
 *
 * @example
 * getLocaleValue(null, 'zh-CN')
 * // => undefined
 *
 * @example
 * getLocaleValue({}, 'zh-CN')
 * // => undefined
 */
export function getLocaleValue(data, locale) {
  if (!data || !locale || typeof data !== 'object' || Array.isArray(data)) return undefined;
  return data[locale] ?? undefined;
}

/**
 * 获取多语言数据的回退值
 *
 * 当当前语言数据为空时，依次尝试默认语言和支持的语言获取回退数据。
 *
 * @param {Object|null|undefined} data - 多语言数据对象，如 { 'zh-CN': { name: '电子产品' } }
 * @param {string} locale - 当前语言代码
 * @param {string} [defaultLocale='en-US'] - 默认语言代码
 * @returns {Object|undefined} 第一个获取到的非空翻译内容，数据为空时返回 undefined
 *
 * @example
 * getFallbackLocaleValue({ 'zh-CN': { name: '中文' }, 'en-US': { name: 'English' } }, 'en-US')
 * // => { name: 'English' }
 *
 * @example
 * getFallbackLocaleValue({ 'zh-CN': { name: '中文' } }, 'en-US')
 * // => { name: '中文' }（回退到默认语言）
 */
export function getFallbackLocaleValue(data, locale, defaultLocale = DEFAULT_LOCALE) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined;
  // 优先使用当前语言
  let result = getLocaleValue(data, locale);
  if (result && Object.keys(result).length > 0) {
    return result;
  }
  // 回退到默认语言
  if (defaultLocale && defaultLocale !== locale) {
    result = getLocaleValue(data, defaultLocale);
    if (result && Object.keys(result).length > 0) {
      return result;
    }
  }
  // 遍历所有支持的语言
  for (const item of SUPPORTED_LOCALES) {
    if (item.code !== locale && item.code !== defaultLocale) {
      result = getLocaleValue(data, item.code);
      if (result && Object.keys(result).length > 0) {
        return result;
      }
    }
  }
  return result || undefined;
}

/**
 * 判断多语言数据是否包含所有指定语言
 *
 * 检查数据对象是否拥有所有语言代码作为顶层 key。
 *
 * @param {Object|null|undefined} data - 多语言数据对象，如 { 'zh-CN': { category_name: '电子产品' } }
 * @param {string[]} [locales] - 语言代码数组，不传则使用 SUPPORTED_LOCALES 的 code 集合
 * @returns {boolean} 是否包含所有语言数据
 *
 * @example
 * hasAllLocales({ 'zh-CN': {}, 'en-US': {}, 'zh-TW': {} })
 * // => true（假设 SUPPORTED_LOCALES 包含这三个）
 *
 * @example
 * hasAllLocales({ 'zh-CN': {} })
 * // => false
 *
 * @example
 * hasAllLocales(null)
 * // => false
 */
export function hasAllLocales(data, locales) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const codes = locales || SUPPORTED_LOCALES.map(l => l.code);
  return codes.every(code => code in data);
}

/**
 * 将数据中嵌套的国际化字段解构到数据顶层
 *
 * 递归遍历数据（数组/对象），找到指定的 key（如 translations），
 * 读取 key 对应对象中以 locale 为属性名的翻译内容，
 * 然后将该翻译内容解构到父级对象上，并删除原始 key。
 *
 * @param {Array | Object} data - 要处理的数据（数组或对象）
 * @param {string} key - 包含国际化翻译的字段名（如 'translations'）
 * @param {string} [locale] - 语言标识，默认从系统缓存读取（zy_locale），再缺省为 'zh-CN'
 * @returns {Array | Object} 处理后的数据（原引用修改并返回）
 *
 * @example
 * // 数组场景
 * const list = [
 *   { id: 1, name: 'x', translations: { 'zh-CN': { category_name: '分类A' } } }
 * ];
 * flattenLocaleData(list, 'translations');
 * // => [{ id: 1, name: 'x', category_name: '分类A' }]
 *
 * @example
 * // 对象场景
 * const obj = { id: 1, translations: { 'zh-CN': { title: '标题' } } };
 * flattenLocaleData(obj, 'translations');
 * // => { id: 1, title: '标题' }
 *
 * @example
 * // 嵌套场景
 * const nested = { items: [{ id: 1, translations: { 'zh-CN': { name: '名称' } } }] };
 * flattenLocaleData(nested, 'translations');
 * // => { items: [{ id: 1, name: '名称' }] }
 */
export function flattenLocaleData(data, key, locale) {
  if (data == null || typeof data !== 'object') return data;

  const resolvedLocale = locale || cache.get(STORAGE_KEYS.LOCALE) || DEFAULT_LOCALE;

  function process(item) {
    if (item == null || typeof item !== 'object') return item;

    // 处理数组：递归每个元素
    if (Array.isArray(item)) {
      item.forEach(process);
      return item;
    }

    // 处理对象：先递归所有子属性，再解构自身
    for (const k of Object.keys(item)) {
      if (k !== key && item[k] != null && typeof item[k] === 'object') {
        process(item[k]);
      }
    }

    // 如果当前对象包含目标 key，解构翻译内容到顶层
    if (key in item) {
      const translations = item[key];
      if (
        translations != null &&
        typeof translations === 'object' &&
        !Array.isArray(translations)
      ) {
        const localized = translations[resolvedLocale];
        if (localized != null && typeof localized === 'object' && !Array.isArray(localized)) {
          Object.assign(item, localized);
        }
      }
      delete item[key];
    }

    return item;
  }

  return process(data);
}

/**
 * 防抖
 * @template T
 * @param {(...args: any[]) => any} fn - 要执行的函数
 * @param {number} [delay=300] - 延迟时间（毫秒）
 * @returns {(...args: any[]) => void} 防抖后的函数
 */
export function debounce(fn, delay = 300) {
  let timer = null;
  return function (...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * 节流
 * @template T
 * @param {(...args: any[]) => any} fn - 要执行的函数
 * @param {number} [delay=300] - 间隔时间（毫秒）
 * @returns {(...args: any[]) => void} 节流后的函数
 */
export function throttle(fn, delay = 300) {
  let last = 0;
  return function (...args) {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn.apply(this, args);
    }
  };
}

/**
 * @param {Array} [options.list] - 要搜索的数组，默认 SUPPORTED_LOCALES
 * @param {string} [options.key] - 匹配的字段名，默认 'code'
 * @returns {Object|undefined} 匹配到的对象，未找到返回 undefined
 *
 * @example
 * getItemByValue('zh-CN')
 * // => { code: 'zh-CN', label: '简体中文' }
 *
 * @example
 * getItemByValue('en-US', { key: 'code' })
 * // => { code: 'en-US', label: 'English' }
 *
 * @example
 * getItemByValue(2, { list: statusList, key: 'id' })
 * // => { id: 2, name: '已发货' }
 */
export function getItemByValue(value, options = {}) {
  const { list = SUPPORTED_LOCALES, key = 'code' } = options;
  return list.find(item => item[key] === value);
}

/**
 * 获取枚举翻译文本（安全处理数字/字符串key）
 *
 * 自动将value转为字符串避免类型陷阱，无匹配兜底fallback。
 *
 * @param {Function} t - vue-i18n 的 t 函数
 * @param {string} path - 翻译路径，如 'enums.giftType'
 * @param {string|number|null|undefined} value - 枚举值
 * @param {string} [fallback='-'] - 无匹配时的兜底文字
 * @returns {string}
 *
 * @example
 * getEnumText(t, 'enums.giftType', 2)   // t('enums.giftType.2')
 * getEnumText(t, 'enums.giftType', '2') // t('enums.giftType.2')
 * getEnumText(t, 'enums.specType', 1)   // t('enums.specType.1')
 */
export function getEnumText(t, path, value, fallback = '-') {
  if (value === '' || value === null || value === undefined) {
    return fallback;
  }
  return t(`${path}.${String(value)}`) || fallback;
}

/**
 * 从 enums.js 自动读取枚举键值生成 select 选项列表
 *
 * 通过 vue-i18n 的 tm() 获取原始消息对象，遍历其所有键生成选项。
 * 新增枚举值只需在 enums.js 中添加翻译，组件无需改动。
 *
 * @param {Function} t  - vue-i18n 的 t 函数
 * @param {Function} tm - vue-i18n 的 tm 函数（获取原始消息对象）
 * @param {string} enumName - 枚举名，如 'giftType'
 * @returns {Array<{value: string, label: string}>}
 *
 * @example
 * // 在组件中：
 * const { t, tm } = useI18n();
 * const options = getEnumOptions(t, tm, 'giftType');
 * // => [{ value: '1', label: '实物礼品' }, { value: '2', label: '虚拟礼品' }]
 */
export function getEnumOptions(t, tm, enumName) {
  const messages = tm(`enums.${enumName}`);
  if (!messages || typeof messages !== 'object') return [];
  return Object.keys(messages).map(key => ({
    value: key,
    label: t(`enums.${enumName}.${key}`),
  }));
}

/**
 * 格式化数字，添加千分位分隔符
 *
 * @param {number | string | null | undefined} num - 要格式化的数值
 * @returns {string} 格式化后的字符串，空值返回 '0'
 *
 * @example
 * formatNumber(12345)   // => '12,345'
 * formatNumber(null)    // => '0'
 * formatNumber(0)       // => '0'
 */
export function formatNumber(num) {
  if (num === undefined || num === null) return '0';
  return Number(num).toLocaleString();
}

/**
 * 下载文件（通过后端返回的文件地址）
 *
 * @param {{ url: string, filename?: string }} options - 文件信息
 * @param {string} options.url - 文件下载地址
 * @param {string} [options.filename] - 文件名，不传则从 URL 末尾提取
 *
 * @example
 * downloadFile({ url: 'https://cdn.example.com/export.xlsx', filename: '报表.xlsx' })
 */
export function downloadFile({ url, filename }) {
  const link = document.createElement('a');
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 判断接口响应是否成功
 *
 * 约定：后端统一返回 { code, data, message } 结构，code === 0 为业务成功。
 *
 * @param {{ code: number, message?: string }} response - 接口返回值
 * @param {boolean|'all'|'success'|'error'} [showMessage=false] - 消息提示模式
 *   - false / 省略：不提示
 *   - true 或 'all'：成功和失败都提示
 *   - 'success'：仅成功时提示
 *   - 'error'：仅失败时提示
 * @param {{ success: Function, error: Function }} [message] - 消息提示实例（如 ant-design-vue 的 message）
 * @returns {boolean} code === 0 返回 true，否则返回 false
 *
 * @example
 * // 仅判断，不提示
 * const ok = isApiSuccess(res);
 *
 * // 全部提示（使用 antd message）
 * const ok = isApiSuccess(res, true, message);
 *
 * // 仅成功时提示
 * const ok = isApiSuccess(res, 'success', message);
 *
 * // 仅失败时提示
 * const ok = isApiSuccess(res, 'error', message);
 */
export function isApiSuccess(response, showMessage = false, message = null) {
  const success = response?.code === 0;

  // 解析 showMessage 模式：true 兼容为 'all'，false 不提示
  const mode =
    showMessage === true
      ? 'all'
      : showMessage === false || showMessage == null
        ? 'none'
        : showMessage;

  if (mode === 'none') {
    return success;
  }

  // 判断当前是否应显示消息
  const shouldShow = (mode === 'all') || (mode === 'success' && success) || (mode === 'error' && !success);

  if (!shouldShow) {
    return success;
  }

  const msg = response?.message || (success ? '操作成功' : '操作失败');
  try {
    if (success && typeof message?.success === 'function') {
      message.success(msg);
    } else if (!success && typeof message?.error === 'function') {
      message.error(msg);
    } else {
      console[success ? 'log' : 'error'](`[API] ${msg}`);
    }
  } catch (e) {
    console.error('[isApiSuccess] message call failed:', e);
  }

  return success;
}
